import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, map } from 'rxjs';
import { catchError, concatMap, switchMap, tap } from 'rxjs/operators';
import * as ReportScheduleActions from './report-schedules.actions';
import { ReportScheduleClientService } from './report-schedules-client.service';
import { ToastService } from '@mode/shared/util-toast';

@Injectable()
export class ReportSchedulesEffects {
  loadReportSchedules$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportScheduleActions.loadReportSchedules),
      switchMap((action) =>
        this.client.loadSchedules(action.reportId).pipe(
          map((response) =>
            ReportScheduleActions.loadReportSchedulesSuccess({
              schedules: response,
            })
          ),
          catchError(() => of(ReportScheduleActions.loadReportSchedulesFailure()))
        )
      )
    );
  });

  deleteReportSchedule$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportScheduleActions.deleteReportSchedule),
      switchMap((action) =>
        this.client.deleteSchedule(action.reportId, action.scheduleId).pipe(
          map(() =>
            ReportScheduleActions.deleteReportScheduleSuccess({
              scheduleId: action.scheduleId,
              successMsg: 'Deleted schedule',
            })
          ),
          catchError(() =>
            of(ReportScheduleActions.deleteReportScheduleFailure({ errorMsg: 'Could not delete schedule' }))
          )
        )
      )
    );
  });

  createEmailSubscriptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportScheduleActions.createEmailSubscription),
      concatMap((action) =>
        this.client.createEmailSubscription(action.url, action.emailPayload).pipe(
          map((response) =>
            ReportScheduleActions.createEmailSubscriptionSuccess({
              scheduleId: action.scheduleId,
              emailSubscription: response,
              subscriberCount: action.subscriberCount,
              successMsg: 'Subscribed',
            })
          ),
          catchError(() => of(ReportScheduleActions.createEmailSubscriptionFailure({ errorMsg: 'Error subscribing' })))
        )
      )
    )
  );

  deleteEmailSubscriptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportScheduleActions.deleteEmailSubscription),
      concatMap((action) =>
        this.client.deleteEmailSubscription(action.url).pipe(
          map(() =>
            ReportScheduleActions.deleteEmailSubscriptionSuccess({
              scheduleId: action.scheduleId,
              successMsg: 'Unsubscribed',
              subscriberCount: action.subscriberCount,
            })
          ),
          catchError(() =>
            of(ReportScheduleActions.deleteEmailSubscriptionFailure({ errorMsg: 'Could not remove email' }))
          )
        )
      )
    )
  );

  successNotifications$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          ReportScheduleActions.deleteReportScheduleSuccess,
          ReportScheduleActions.createEmailSubscriptionSuccess,
          ReportScheduleActions.deleteEmailSubscriptionSuccess
        ),
        tap((action) => this.toastService.success(action.successMsg))
      );
    },
    { dispatch: false }
  );

  errorNotifications$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          ReportScheduleActions.deleteReportScheduleFailure,
          ReportScheduleActions.createEmailSubscriptionFailure,
          ReportScheduleActions.deleteEmailSubscriptionFailure
        ),
        tap((action) => this.toastService.error(action.errorMsg))
      );
    },
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private client: ReportScheduleClientService,
    private toastService: ToastService
  ) {}
}
